import { UPLOAD_RETENTION_MS } from '@buffer-mono/shared-utils/uploads'

import type { PrefillPostData } from '~publish/hooks/usePostComposer'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft/Draft'
import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import ComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'
import { draftDataToRpcUpdate } from '~publish/legacy/composer/composer/utils/PreviewUtils'

const AUTO_SAVE_KEY = 'composerAutoSave'

/** Bump whenever AutoSaveData's shape changes in a way a reader needs to branch on. */
export const AUTO_SAVE_VERSION = 1

/** Dispatched on `window` whenever the auto-save is written or cleared, so same-tab listeners (e.g. useHasComposerAutoSave) can react without a `storage` event, which only fires cross-tab. */
export const AUTO_SAVE_CHANGE_EVENT = 'publish:composerAutoSaveChanged'

type DraftData = Pick<
  Draft,
  | 'text'
  | 'updateType'
  | 'images'
  | 'video'
  | 'videos'
  | 'urls'
  | 'gif'
  | 'document'
  | 'title'
  | 'channelData'
  | 'unshortenedUrls'
  | 'places'
  | 'thread'
> & {
  tags?: string[]
}

/** Per-channel draft data (same shape as DraftData, without tags which are global). */
export type PerChannelDraftData = Omit<DraftData, 'tags'>

export type AutoSaveData = {
  /**
   * Absent on entries written before this field existed. `saveComposerAutoSave`
   * always stamps the current version; `getComposerAutoSave` backfills 1 (the
   * shape hasn't changed since) on read so an un-versioned entry already on a
   * user's disk still recovers normally.
   */
  version?: number
  draft: DraftData
  selectedChannelIds: string[]
  savedAt: number
  /** Per-channel draft data when user had customized per-channel. Keyed by channel ID. */
  perChannelDrafts?: Record<string, PerChannelDraftData>
}

export function serializeDraft(draft: Draft): PerChannelDraftData {
  return {
    text: draft.text,
    updateType: draft.updateType,
    images: draft.images,
    video: draft.video,
    videos: draft.videos,
    urls: draft.urls,
    gif: draft.gif,
    document: draft.document,
    title: draft.title,
    channelData: draft.channelData,
    unshortenedUrls: draft.unshortenedUrls,
    places: draft.places,
    thread: draft.thread,
  }
}

export function saveComposerAutoSave(
  draft: Draft,
  tags: string[],
  selectedChannelIds: string[],
  perChannelDrafts?: Record<string, PerChannelDraftData>,
): void {
  const data: AutoSaveData = {
    version: AUTO_SAVE_VERSION,
    draft: {
      ...serializeDraft(draft),
      tags,
    },
    selectedChannelIds,
    savedAt: Date.now(),
    ...(perChannelDrafts && { perChannelDrafts }),
  }
  localStorage.setItem(AUTO_SAVE_KEY, JSON.stringify(data))
  window.dispatchEvent(new Event(AUTO_SAVE_CHANGE_EVENT))
}

function isAutoSaveData(value: unknown): value is AutoSaveData {
  if (typeof value !== 'object' || value === null) return false
  if ('version' in value && typeof value.version !== 'number') return false
  if (!('savedAt' in value) || typeof value.savedAt !== 'number') return false
  if (
    !('selectedChannelIds' in value) ||
    !Array.isArray(value.selectedChannelIds)
  ) {
    return false
  }
  if (
    !('draft' in value) ||
    typeof value.draft !== 'object' ||
    value.draft === null
  ) {
    return false
  }
  return 'text' in value.draft && typeof value.draft.text === 'string'
}

function isDraftDataEmpty(
  data: Partial<PerChannelDraftData> | undefined,
): boolean {
  if (!data) return true
  if (typeof data.text === 'string' && data.text.length > 0) return false
  if (Array.isArray(data.images) && data.images.length > 0) return false
  if (data.video) return false
  if (Array.isArray(data.videos) && data.videos.length > 0) return false
  if (data.gif) return false
  if (data.document) return false
  if (data.title) return false
  if (Array.isArray(data.thread) && data.thread.some((t) => t?.text?.length))
    return false
  return true
}

export function hasUserContent(data: AutoSaveData): boolean {
  if (data.perChannelDrafts) {
    return Object.values(data.perChannelDrafts).some(
      (d) => !isDraftDataEmpty(d),
    )
  }
  return !isDraftDataEmpty(data.draft)
}

export function getComposerAutoSave(): AutoSaveData | null {
  try {
    const raw = localStorage.getItem(AUTO_SAVE_KEY)
    if (!raw) return null

    const data: unknown = JSON.parse(raw)
    if (!isAutoSaveData(data)) {
      localStorage.removeItem(AUTO_SAVE_KEY)
      return null
    }

    if (Date.now() - data.savedAt > UPLOAD_RETENTION_MS) {
      localStorage.removeItem(AUTO_SAVE_KEY)
      return null
    }

    // Drop zombie entries: a per-channel save where every per-channel draft is
    // empty (or an omni save with no content) is not recoverable user work.
    if (!hasUserContent(data)) {
      localStorage.removeItem(AUTO_SAVE_KEY)
      return null
    }

    return data.version === undefined ? { ...data, version: 1 } : data
  } catch {
    localStorage.removeItem(AUTO_SAVE_KEY)
    return null
  }
}

export function clearComposerAutoSave(): void {
  localStorage.removeItem(AUTO_SAVE_KEY)
  window.dispatchEvent(new Event(AUTO_SAVE_CHANGE_EVENT))
}

const PREVIEW_MAX_LENGTH = 120

/**
 * Picks a representative text snippet from an auto-save entry for the
 * recovery dialog preview. Prefers the first non-empty per-channel draft
 * when present (those reflect what the user actually wrote), otherwise
 * falls back to the omni draft text. Truncated to ~180 chars with an
 * ellipsis.
 */
export function getAutoSavePreviewText(
  data: AutoSaveData,
  maxLength: number = PREVIEW_MAX_LENGTH,
): string {
  let text = ''
  if (data.perChannelDrafts) {
    const firstWithText = Object.values(data.perChannelDrafts).find(
      (d) => typeof d.text === 'string' && d.text.length > 0,
    )
    text = firstWithText?.text ?? data.draft.text ?? ''
  } else {
    text = data.draft.text ?? ''
  }
  if (text.length > maxLength) {
    return `${text.slice(0, maxLength).trimEnd()}…`
  }
  return text
}

/**
 * Returns whether the composer currently holds content visible to the user.
 * In omni mode only the omni draft matters; in per-channel mode the omni draft
 * is hidden so we ignore it and inspect the enabled per-channel drafts.
 */
export function hasVisibleDraftContent(): boolean {
  if (AppStore.getIsOmniboxEnabled() === true) {
    const omniDraft = ComposerStore.getDraft('omni')
    return !!omniDraft && !omniDraft.isEmpty()
  }
  return ComposerStore.getEnabledDrafts().some(
    (d) => !d.service.isOmni && !d.isEmpty(),
  )
}

export function autoSaveDataToPrefillPostData(data: AutoSaveData): {
  prefillPostData: PrefillPostData
  selectedChannelIds: string[]
} {
  return {
    prefillPostData: draftDataToRpcUpdate(data.draft),
    selectedChannelIds: data.selectedChannelIds,
  }
}
